/**
 * Comprehensive Customer Form — used in both Customers list (add) and
 * CustomerProfile (edit). Collapsible sections to keep mobile UX manageable.
 *
 * Inline photo + CNIC F/B uploaders live next to Name + CNIC fields.
 * Two upload modes:
 *  - Edit: customerId present → uploads immediately (live mode).
 *  - Add:  no customerId → files staged in parent via `stagedDocs` + `onStageDoc`.
 */
import React, { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { ChevronDown, ChevronRight, User2, Briefcase, ShieldCheck, FileText, UserPlus, Search, AlertTriangle } from "lucide-react";
import InlinePhotoUpload from "./InlinePhotoUpload";
import BlacklistWarning from "./BlacklistWarning";
import GuarantorPickerDialog from "./GuarantorPickerDialog";
import { api } from "../lib/api";

export const RESIDENCE_TYPES = [
  { value: "own_house", label: "Own House" },
  { value: "rented_house", label: "Rented House" },
  { value: "family_house", label: "Family House" },
  { value: "company_accommodation", label: "Company Accommodation" },
  { value: "other", label: "Other" },
];

export const BUSINESS_TYPES = [
  { value: "mobile_shop", label: "Mobile Shop" },
  { value: "electronics_shop", label: "Electronics Shop" },
  { value: "employee", label: "Employee" },
  { value: "self_employed", label: "Self Employed" },
  { value: "student", label: "Student" },
  { value: "other", label: "Other" },
];

export const RELATIONSHIPS = [
  "father", "mother", "brother", "sister", "husband", "wife",
  "friend", "relative", "employer", "other",
];

export const emptyGuarantor = () => ({
  name: "", relationship: "", father_husband_name: "", cnic: "",
  residence_address: "", office_address: "", designation: "",
  mobile: "", telephone: "",
});

export const emptyCustomer = () => ({
  name: "", mobile: "", alternate_mobile: "", cnic: "",
  father_husband_name: "", address: "", near_landmark: "",
  residence_type: "other", years_at_current_address: "", monthly_house_rent: "",
  electricity_meter_number: "", gas_meter_number: "",
  account_number: "", reference_account_number: "", inspector_name: "",
  business_type: "other", business_name: "", monthly_income: "",
  business_address: "", years_in_business: "", office_name: "",
  designation: "", office_address: "", office_telephone: "",
  guarantor1: emptyGuarantor(),
  guarantor2: null,
  recovery_zone: "", assigned_agent_id: "", recovery_notes: "", field_visit_notes: "",
});

// Document slot keys understood by the parent submit flow.
// Keep these in sync with backend `routers/customers.py` slot definitions.
export const CUSTOMER_DOC_KEYS = ["photo", "cnic_front", "cnic_back"];
export const GUARANTOR_DOC_KEYS = ["photo", "cnic_front", "cnic_back"];

function Section({ title, icon: Icon, children, defaultOpen = true, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-md bg-white" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-slate-50"
      >
        {Icon && <Icon className="w-4 h-4 text-emerald-600" />}
        <div className="flex-1 font-display font-semibold text-slate-900">{title}</div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="p-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-3">
          {children}
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
      <Label>{label} {required && <span className="text-red-500">*</span>}</Label>
      {children}
    </div>
  );
}

function SelectField({ value, onChange, options, placeholder = "Select…" }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          typeof o === "string"
            ? <SelectItem key={o} value={o}>{o.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())}</SelectItem>
            : <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Three side-by-side photo uploaders: Photo, CNIC Front, CNIC Back.
 * Used for both customer + each guarantor. Switches between live + staged mode
 * based on whether endpointFor() returns a non-null endpoint.
 */
function DocsRow({ docs, endpointFor, stagedDocs, onStageDoc, slots, idPrefix, onUploaded }) {
  return (
    <div className="md:col-span-2 grid grid-cols-3 gap-3" data-testid={`${idPrefix}-docs-row`}>
      {slots.map(([slot, label]) => {
        const ep = endpointFor ? endpointFor(slot) : null;
        if (ep) {
          return (
            <InlinePhotoUpload
              key={slot}
              label={label}
              endpoint={ep}
              currentUrl={docs?.[slot]}
              onUploaded={onUploaded}
              testId={`${idPrefix}-${slot}`}
              variant="wide"
            />
          );
        }
        return (
          <InlinePhotoUpload
            key={slot}
            label={label}
            stagedFile={stagedDocs?.[slot] || null}
            onStage={(file) => onStageDoc?.(slot, file)}
            currentUrl={docs?.[slot]}
            testId={`${idPrefix}-${slot}`}
            variant="wide"
          />
        );
      })}
    </div>
  );
}

const CUST_SLOTS = [
  ["photo", "Customer Photo"],
  ["cnic_front", "CNIC Front"],
  ["cnic_back", "CNIC Back"],
];
const GUAR_SLOTS = [
  ["photo", "Guarantor Photo"],
  ["cnic_front", "CNIC Front"],
  ["cnic_back", "CNIC Back"],
];

export default function CustomerForm({
  value, onChange, agents = [],
  customerId,                  // present in edit mode → enables live uploads
  stagedDocs, onStageDoc,      // staged-mode for new-customer flow
  onDocsUploaded,              // live-mode callback to refresh parent state
  onBlacklistResult,           // bubble result up so parent can disable Save in block-mode
}) {
  const v = value;
  const set = (patch) => onChange({ ...v, ...patch });
  const setG = (which, patch) => onChange({ ...v, [which]: { ...(v[which] || emptyGuarantor()), ...patch } });
  // Apply a picked central-pool guarantor into the form. Stores both the
  // canonical id (so the backend re-syncs the snapshot) AND the data inline
  // (so the UI reflects the choice immediately).
  const applyPickedGuarantor = (which, g) => {
    const idKey = `${which}_id`;
    onChange({
      ...v,
      [idKey]: g?.id || null,
      [which]: g ? {
        name: g.name || "",
        relationship: g.relationship || "",
        father_husband_name: g.father_husband_name || "",
        cnic: g.cnic || "",
        mobile: g.mobile || "",
        telephone: g.telephone || "",
        residence_address: g.residence_address || g.address || "",
        office_address: g.office_address || "",
        designation: g.designation || g.occupation || "",
        documents: g.documents || {},
        verification_status: v[which]?.verification_status || "pending",
      } : emptyGuarantor(),
    });
  };
  // Reset a slot to a fresh empty form + clear the link id
  const resetGuarantor = (which) => {
    const idKey = `${which}_id`;
    onChange({ ...v, [idKey]: null, [which]: emptyGuarantor() });
  };
  const enableG2 = !!v.guarantor2;
  const isRented = v.residence_type === "rented_house";

  // Endpoint resolvers (live mode). Return null when no customerId yet → staged mode.
  const custEndpoint = (slot) => (customerId ? `/customers/${customerId}/documents/${slot}` : null);
  const g1Endpoint = (slot) => (customerId ? `/customers/${customerId}/guarantor/1/documents/${slot}` : null);
  const g2Endpoint = (slot) => (customerId ? `/customers/${customerId}/guarantor/2/documents/${slot}` : null);

  const stageCust = (slot, file) => onStageDoc?.("customer", slot, file);
  const stageG1 = (slot, file) => onStageDoc?.("guarantor1", slot, file);
  const stageG2 = (slot, file) => onStageDoc?.("guarantor2", slot, file);

  return (
    <div className="space-y-3" data-testid="customer-form">
      <BlacklistWarning cnic={v.cnic} mobile={v.mobile} onResult={onBlacklistResult} />
      <Section title="Basic Details" icon={User2} testId="form-basic">
        <Field label="Customer Name" required>
          <Input required value={v.name} onChange={(e) => set({ name: e.target.value })} data-testid="cust-name-input" />
        </Field>
        <Field label="Mobile" required>
          <Input required value={v.mobile} onChange={(e) => set({ mobile: e.target.value })} data-testid="cust-mobile-input" />
        </Field>

        {/* Photo + CNIC F/B uploads grouped here, directly under Name/Mobile */}
        <DocsRow
          docs={v.documents}
          endpointFor={custEndpoint}
          stagedDocs={stagedDocs?.customer}
          onStageDoc={stageCust}
          slots={CUST_SLOTS}
          idPrefix="cust"
          onUploaded={onDocsUploaded}
        />

        <Field label="Alternate Mobile">
          <Input value={v.alternate_mobile || ""} onChange={(e) => set({ alternate_mobile: e.target.value })} data-testid="cust-alt-mobile-input" />
        </Field>
        <Field label="CNIC">
          <Input value={v.cnic || ""} onChange={(e) => set({ cnic: e.target.value })} placeholder="42101-1234567-1" data-testid="cust-cnic-input" />
        </Field>
        <Field label="Father / Husband Name">
          <Input value={v.father_husband_name || ""} onChange={(e) => set({ father_husband_name: e.target.value })} />
        </Field>
        <Field label="Address" full>
          <Input value={v.address || ""} onChange={(e) => set({ address: e.target.value })} />
        </Field>
        <Field label="Near Landmark">
          <Input value={v.near_landmark || ""} onChange={(e) => set({ near_landmark: e.target.value })} />
        </Field>
        <Field label="Residence Type" required>
          <SelectField
            value={v.residence_type}
            onChange={(val) => set({ residence_type: val })}
            options={RESIDENCE_TYPES}
          />
        </Field>
        <Field label="Years at Current Address">
          <Input type="number" min="0" step="0.5" value={v.years_at_current_address || ""} onChange={(e) => set({ years_at_current_address: e.target.value })} />
        </Field>
        {isRented && (
          <Field label="Monthly House Rent">
            <Input type="number" min="0" value={v.monthly_house_rent || ""} onChange={(e) => set({ monthly_house_rent: e.target.value })} data-testid="cust-rent-input" />
          </Field>
        )}
        <Field label="Electricity Meter Number">
          <Input value={v.electricity_meter_number || ""} onChange={(e) => set({ electricity_meter_number: e.target.value })} />
        </Field>
        <Field label="Gas Meter Number">
          <Input value={v.gas_meter_number || ""} onChange={(e) => set({ gas_meter_number: e.target.value })} />
        </Field>
      </Section>

      <Section title="Account Information" icon={FileText} testId="form-account">
        <Field label="Account Number">
          <Input value={v.account_number || ""} onChange={(e) => set({ account_number: e.target.value })} data-testid="cust-acct-input" />
        </Field>
        <Field label="Reference Account Number">
          <Input value={v.reference_account_number || ""} onChange={(e) => set({ reference_account_number: e.target.value })} />
        </Field>
        <Field label="Inspector Name">
          <Input value={v.inspector_name || ""} onChange={(e) => set({ inspector_name: e.target.value })} />
        </Field>
      </Section>

      <Section title="Employment / Business Information" icon={Briefcase} testId="form-employment">
        <Field label="Business Type">
          <SelectField
            value={v.business_type}
            onChange={(val) => set({ business_type: val })}
            options={BUSINESS_TYPES}
          />
        </Field>
        <Field label="Business Name">
          <Input value={v.business_name || ""} onChange={(e) => set({ business_name: e.target.value })} />
        </Field>
        <Field label="Monthly Income">
          <Input type="number" min="0" value={v.monthly_income || ""} onChange={(e) => set({ monthly_income: e.target.value })} data-testid="cust-income-input" />
        </Field>
        <Field label="Years in Business">
          <Input type="number" min="0" step="0.5" value={v.years_in_business || ""} onChange={(e) => set({ years_in_business: e.target.value })} />
        </Field>
        <Field label="Business Address" full>
          <Input value={v.business_address || ""} onChange={(e) => set({ business_address: e.target.value })} />
        </Field>
        <Field label="Office Name">
          <Input value={v.office_name || ""} onChange={(e) => set({ office_name: e.target.value })} />
        </Field>
        <Field label="Designation">
          <Input value={v.designation || ""} onChange={(e) => set({ designation: e.target.value })} />
        </Field>
        <Field label="Office Address" full>
          <Input value={v.office_address || ""} onChange={(e) => set({ office_address: e.target.value })} />
        </Field>
        <Field label="Office Telephone">
          <Input value={v.office_telephone || ""} onChange={(e) => set({ office_telephone: e.target.value })} />
        </Field>
      </Section>

      <GuarantorSection
        title="Guarantor 1 Information"
        value={v.guarantor1 || emptyGuarantor()}
        linkedId={v.guarantor1_id}
        onChange={(patch) => setG("guarantor1", patch)}
        onPick={(g) => applyPickedGuarantor("guarantor1", g)}
        onClear={() => resetGuarantor("guarantor1")}
        idx={1}
        required
        endpointFor={g1Endpoint}
        stagedDocs={stagedDocs?.guarantor1}
        onStageDoc={stageG1}
        onDocsUploaded={onDocsUploaded}
      />

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="p-4 flex items-center justify-between">
          <div className="font-display font-semibold text-slate-900">Guarantor 2 (Optional)</div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => set({ guarantor2: enableG2 ? null : emptyGuarantor(), guarantor2_id: enableG2 ? null : v.guarantor2_id })}
            data-testid="toggle-g2-btn"
          >
            {enableG2 ? "Remove Guarantor 2" : "+ Add Guarantor 2"}
          </Button>
        </div>
        {enableG2 && (
          <div className="border-t border-slate-100">
            <GuarantorSection
              title=""
              value={v.guarantor2 || emptyGuarantor()}
              linkedId={v.guarantor2_id}
              onChange={(patch) => setG("guarantor2", patch)}
              onPick={(g) => applyPickedGuarantor("guarantor2", g)}
              onClear={() => resetGuarantor("guarantor2")}
              idx={2}
              barebones
              endpointFor={g2Endpoint}
              stagedDocs={stagedDocs?.guarantor2}
              onStageDoc={stageG2}
              onDocsUploaded={onDocsUploaded}
            />
          </div>
        )}
      </div>

      <Section title="Recovery Information" icon={ShieldCheck} testId="form-recovery">
        <Field label="Recovery Zone">
          <Input value={v.recovery_zone || ""} onChange={(e) => set({ recovery_zone: e.target.value })} placeholder="e.g., Zone 5 - Gulshan" />
        </Field>
        <Field label="Assigned Recovery Agent">
          <SelectField
            value={v.assigned_agent_id || "__none__"}
            onChange={(val) => set({ assigned_agent_id: val === "__none__" ? null : val })}
            options={[{ value: "__none__", label: "— Not assigned —" }, ...agents.map((a) => ({ value: a.id, label: `${a.name} · ${a.recovery_zone || "no zone"}` }))]}
          />
        </Field>
        <Field label="Recovery Notes" full>
          <Textarea value={v.recovery_notes || ""} onChange={(e) => set({ recovery_notes: e.target.value })} rows={2} />
        </Field>
        <Field label="Field Visit Notes" full>
          <Textarea value={v.field_visit_notes || ""} onChange={(e) => set({ field_visit_notes: e.target.value })} rows={2} />
        </Field>
      </Section>
    </div>
  );
}

function GuarantorSection({
  title, value, linkedId, onChange, onPick, onClear, idx, required, barebones,
  endpointFor, stagedDocs, onStageDoc, onDocsUploaded,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dup, setDup] = useState(null);
  const [dupChecking, setDupChecking] = useState(false);
  const dupTimer = useRef(null);
  const lastCheckRef = useRef("");
  const set = (patch) => onChange(patch);

  // Debounced duplicate detection. Skips when slot is already linked.
  useEffect(() => {
    if (linkedId) { setDup(null); return; }
    const cnic = (value?.cnic || "").trim();
    const mobile = (value?.mobile || "").trim();
    const key = `${cnic}|${mobile}`;
    if (key === lastCheckRef.current) return;
    if (!cnic && !mobile) { setDup(null); return; }
    clearTimeout(dupTimer.current);
    dupTimer.current = setTimeout(async () => {
      lastCheckRef.current = key;
      setDupChecking(true);
      try {
        const { data } = await api.post("/guarantors/check-duplicate", { cnic, mobile });
        setDup(data?.match ? data : null);
      } catch {/* ignore */} finally {
        setDupChecking(false);
      }
    }, 450);
    return () => clearTimeout(dupTimer.current);
  }, [value?.cnic, value?.mobile, linkedId]);

  const useExisting = () => {
    if (!dup?.match) return;
    onPick?.(dup.match);
    setDup(null);
  };

  const linkedBanner = linkedId && (
    <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2.5 text-xs text-emerald-800 flex items-center justify-between gap-2" data-testid={`g${idx}-linked-banner`}>
      <span className="flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" />
        <span>Linked to <span className="font-bold">{value.name}</span> from central pool.</span>
      </span>
      <Button type="button" size="sm" variant="ghost" onClick={() => onClear?.()} className="h-6 text-emerald-700 hover:text-emerald-900 px-2">
        Change
      </Button>
    </div>
  );

  const pickerToolbar = (
    <div className="flex flex-wrap gap-2 mb-3 md:col-span-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setPickerOpen(true)}
        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
        data-testid={`g${idx}-pick-existing-btn`}
      >
        <Search className="w-3.5 h-3.5 mr-1.5" /> Select Existing Guarantor
      </Button>
      {!linkedId && (
        <span className="text-xs text-slate-500 self-center">— ya neeche form fill karein</span>
      )}
      {linkedId && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => onClear?.()}
          className="text-slate-600 hover:text-slate-900"
          data-testid={`g${idx}-clear-link-btn`}
        >
          <UserPlus className="w-3.5 h-3.5 mr-1" /> Add as New
        </Button>
      )}
    </div>
  );

  const dupBanner = (!linkedId && dup?.match) && (
    <div className="rounded-md bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900 md:col-span-2 space-y-2" data-testid={`g${idx}-duplicate-banner`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-bold">An existing guarantor was found.</div>
          <div className="mt-0.5">
            <span className="font-semibold">{dup.match.name}</span> · {dup.match.cnic || "no CNIC"} · {dup.match.mobile || "no mobile"} —
            {" "}{dup.match.customers_linked || 0} customer(s), {dup.match.active_loans || 0} active loan(s).
            Would you like to use this guarantor instead?
          </div>
        </div>
      </div>
      <div className="flex gap-2 pl-6">
        <Button type="button" size="sm" onClick={useExisting} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7" data-testid={`g${idx}-use-existing-btn`}>
          Use Existing Guarantor
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => setDup(null)} className="h-7 border-slate-300" data-testid={`g${idx}-create-anyway-btn`}>
          Create Anyway
        </Button>
      </div>
    </div>
  );

  const fields = (
    <>
      {pickerToolbar}
      {linkedBanner && <div className="md:col-span-2">{linkedBanner}</div>}
      {dupBanner}
      <Field label="Guarantor Name" required={required}>
        <Input required={required} value={value.name || ""} onChange={(e) => set({ name: e.target.value })} data-testid={`g${idx}-name-input`} />
      </Field>
      <Field label="Relationship">
        <SelectField
          value={value.relationship}
          onChange={(val) => set({ relationship: val })}
          options={RELATIONSHIPS}
        />
      </Field>

      {/* Inline Guarantor photo + CNIC F/B */}
      <DocsRow
        docs={value.documents}
        endpointFor={endpointFor}
        stagedDocs={stagedDocs}
        onStageDoc={onStageDoc}
        slots={GUAR_SLOTS}
        idPrefix={`g${idx}`}
        onUploaded={onDocsUploaded}
      />

      <Field label="Father / Husband Name">
        <Input value={value.father_husband_name || ""} onChange={(e) => set({ father_husband_name: e.target.value })} />
      </Field>
      <Field label="CNIC">
        <Input value={value.cnic || ""} onChange={(e) => set({ cnic: e.target.value })} data-testid={`g${idx}-cnic-input`} />
      </Field>
      <Field label="Mobile">
        <Input value={value.mobile || ""} onChange={(e) => set({ mobile: e.target.value })} data-testid={`g${idx}-mobile-input`} />
      </Field>
      <Field label="Telephone">
        <Input value={value.telephone || ""} onChange={(e) => set({ telephone: e.target.value })} />
      </Field>
      <Field label="Residence Address" full>
        <Input value={value.residence_address || ""} onChange={(e) => set({ residence_address: e.target.value })} />
      </Field>
      <Field label="Office Address" full>
        <Input value={value.office_address || ""} onChange={(e) => set({ office_address: e.target.value })} />
      </Field>
      <Field label="Designation">
        <Input value={value.designation || ""} onChange={(e) => set({ designation: e.target.value })} />
      </Field>
      {dupChecking && (
        <div className="md:col-span-2 text-[11px] text-slate-400">Checking guarantor pool…</div>
      )}
    </>
  );
  const wrapped = barebones ? (
    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`guarantor-${idx}-section`}>{fields}</div>
  ) : (
    <Section title={title} icon={ShieldCheck} testId={`guarantor-${idx}-section`}>{fields}</Section>
  );
  return (
    <>
      {wrapped}
      <GuarantorPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={(g) => onPick?.(g)}
        onCreateNew={() => onClear?.()}
        slotLabel={`Guarantor ${idx}`}
      />
    </>
  );
}
